import { marked } from 'marked';
import { Tag } from "./types";
import { parseFrontmatter } from './frontmatter';

export interface Project {
  id: string;
  title: string;
  description: string;
  tags: Tag[];
  content: string;
  slug: string;
  date: string;
  order?: number;
  sourceUrl?: string;
  demoUrl?: string;
  docsUrl?: string;
  pinned?: boolean;
}

// Define types for the import.meta.glob result
type GlobModule = {
  [key: string]: string;
};

// Type-safe glob import
const projectModules: GlobModule = import.meta.glob('/src/content/projects/*.md', {
  eager: true,
  as: 'raw'
}) as GlobModule;

// Configure marked options
marked.use({
  mangle: false,
  headerIds: false
});

export async function getAllProjects(): Promise<{
  pinnedProjects: Project[];
  otherProjects: Project[];
}> {
  const projects = await Promise.all(
    Object.entries(projectModules).map(async ([filepath, content]: [string, string]) => {
      const slug = filepath.replace(/^.*\/(.+)\.md$/, '$1');
      const { frontmatter, body } = parseFrontmatter(content);
      const htmlContent = await marked(body);

      // Convert pinned to boolean explicitly
      const isPinned = frontmatter.pinned === true;

      // Convert string tags to Tag objects when parsing frontmatter
      const parsedTags: Tag[] = ((frontmatter.tags as string[]) || []).map((tag: string) => ({
        name: tag
      }));

      return {
        id: slug,
        title: (frontmatter.title as string) ?? '',
        description: (frontmatter.description as string) ?? '',
        tags: parsedTags,
        pinned: isPinned,
        content: htmlContent,
        slug: slug,
        date: (frontmatter.date as string) ?? '',
        order: Number(frontmatter.order) || 999,
        demoUrl: (frontmatter.demoUrl as string) ?? '',
        sourceUrl: (frontmatter.sourceUrl as string) ?? '',
        docsUrl: (frontmatter.docsUrl as string) ?? ''
      } as Project;
    })
  );

  // Sort projects by order (ascending), then by date (newest first)
  const sortedProjects = projects.sort((a, b) =>
    (a.order ?? 999) - (b.order ?? 999) || new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return {
    pinnedProjects: sortedProjects.filter(p => p.pinned === true),
    otherProjects: sortedProjects.filter(p => p.pinned !== true)
  };
}
